<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Copy, ExternalLink, Link2, Pencil, Plus, Power, RefreshCw, ShieldCheck } from '@lucide/vue'
import { Badge, Button, EmptyState, FormField, Modal, PageHeader, Section, Select, Spinner, Table, useToast } from '@drama/ui'
import { listPromotions, savePromotion, setTrialEnabled } from '../api'

const { t } = useI18n()
const toast = useToast()
const data = ref({ links: [], risks: [], games: [], trial_enabled: false })
const loading = ref(true)
const saving = ref(false)
const refreshing = ref(false)
const switching_trial = ref(false)
const toggling_link_id = ref('')
const error = ref('')
const open = ref(false)
const form = reactive({ id: '', name: '', scenario_id: '', platform: 'TikTok', video_url: '', enabled: true })
const stages = ['open', 'loaded', 'interaction', 'limit_reached', 'registered', 'existing_login', 'continued']
const columns = computed(() => [
  { key: 'name', label: t('visualGame.promotion.admin.name'), width: '240px', wrap: true },
  { key: 'link', label: t('visualGame.promotion.admin.link'), width: '270px', wrap: true },
  ...stages.map(key => ({ key, label: t(`visualGame.promotion.admin.stages.${key}`), width: '120px', align: 'right' })),
  { key: 'actions', label: t('visualGame.promotion.admin.actions'), width: '210px', fixed: 'right' },
])
const risk_columns = computed(() => [
  { key: 'created_at', label: t('visualGame.promotion.admin.time'), width: '220px' },
  { key: 'risk_type', label: t('visualGame.promotion.admin.risk'), width: '320px' },
  { key: 'run_id', label: t('visualGame.promotion.admin.run'), width: '280px', wrap: true },
])
const game_options = computed(() => data.value.games.map(game => ({ value: game.id, label: game.title })))
const shortUrl = row => row.short_url

onMounted(load)
async function load(background = false) {
  if (!background) loading.value = true
  error.value = ''
  try { data.value = await listPromotions() }
  catch (reason) { error.value = reason.message }
  finally { if (!background) loading.value = false }
}
async function refresh() {
  refreshing.value = true
  try { await load(true) }
  finally { refreshing.value = false }
}
function edit(row) {
  Object.assign(form, row ? { id: row.id, name: row.name, scenario_id: row.scenario_id, platform: row.platform, video_url: row.video_url || '', enabled: row.enabled } : { id: '', name: '', scenario_id: '', platform: 'TikTok', video_url: '', enabled: true })
  open.value = true
}
async function save() {
  saving.value = true
  try {
    const { id, ...body } = form
    await savePromotion(id, { ...body, video_url: body.video_url || null })
    await load(true)
    open.value = false
  } catch (reason) { toast.error(reason.message) }
  finally { saving.value = false }
}
async function toggle(row) {
  toggling_link_id.value = row.id
  try {
    await savePromotion(row.id, { name: row.name, scenario_id: row.scenario_id, platform: row.platform, video_url: row.video_url, enabled: !row.enabled })
    await load(true)
  } catch (reason) { toast.error(reason.message) }
  finally { toggling_link_id.value = '' }
}
async function switchTrial() {
  switching_trial.value = true
  try { await setTrialEnabled(!data.value.trial_enabled); await load(true) }
  catch (reason) { toast.error(reason.message) }
  finally { switching_trial.value = false }
}
async function copy(row) {
  try { await navigator.clipboard.writeText(shortUrl(row)); toast.success(t('visualGame.promotion.admin.copied')) }
  catch { toast.error(t('visualGame.promotion.admin.copyFailed')) }
}
</script>

<template>
  <div class="promotions-page">
    <div class="page-heading">
      <PageHeader :title="t('visualGame.promotion.admin.title')">
        <template #actions>
          <Button variant="secondary" :loading="refreshing" :disabled="loading || saving || switching_trial || !!toggling_link_id" @click="refresh"><RefreshCw class="button-icon" aria-hidden="true" />{{ t('visualGame.promotion.admin.refresh') }}</Button>
          <Button :disabled="loading || saving" data-testid="promotion-create" @click="edit()"><Plus class="button-icon" aria-hidden="true" />{{ t('visualGame.promotion.admin.create') }}</Button>
        </template>
      </PageHeader>
      <p class="muted">{{ t('visualGame.promotion.admin.description') }}</p>
    </div>
    <p v-if="error" class="error-message" role="alert">{{ error }}</p>
    <div v-if="loading" class="loading-state"><Spinner /></div>
    <template v-else-if="!error">
      <Section class="trial-panel">
        <div class="trial-switch">
          <div class="panel-icon" :class="{ 'is-enabled': data.trial_enabled }"><ShieldCheck aria-hidden="true" /></div>
          <div class="trial-copy"><h2>{{ t('visualGame.promotion.admin.switchTitle') }}</h2><p class="muted">{{ t('visualGame.promotion.admin.switchHint') }}</p></div>
          <div class="trial-actions">
            <Badge :variant="data.trial_enabled ? 'success' : 'warning'">{{ t(data.trial_enabled ? 'visualGame.promotion.admin.enabled' : 'visualGame.promotion.admin.disabled') }}</Badge>
            <Button :loading="switching_trial" :disabled="saving || refreshing" variant="secondary" data-testid="promotion-global-switch" @click="switchTrial"><Power class="button-icon" aria-hidden="true" />{{ t(data.trial_enabled ? 'visualGame.promotion.admin.closeTrial' : 'visualGame.promotion.admin.openTrial') }}</Button>
          </div>
        </div>
      </Section>
      <Section :title="t('visualGame.promotion.admin.linksTitle')" :padded="false">
        <template #actions><span class="section-count">{{ data.links.length }}</span></template>
        <div v-if="!data.links.length" class="links-empty">
          <div class="panel-icon"><Link2 aria-hidden="true" /></div>
          <h3>{{ t('visualGame.promotion.admin.empty') }}</h3>
          <p class="muted">{{ t('visualGame.promotion.admin.emptyHint') }}</p>
          <Button variant="secondary" @click="edit()"><Plus class="button-icon" aria-hidden="true" />{{ t('visualGame.promotion.admin.create') }}</Button>
        </div>
        <Table v-else :columns="columns" :rows="data.links">
          <template #name="{ row }"><div class="link-identity"><strong>{{ row.name }}</strong><span class="muted">{{ row.platform }}</span><Badge :variant="row.enabled ? 'success' : 'neutral'">{{ t(row.enabled ? 'visualGame.promotion.admin.linkEnabled' : 'visualGame.promotion.admin.linkDisabled') }}</Badge></div></template>
          <template #link="{ row }"><div class="link-address"><code>{{ shortUrl(row) }}</code><div class="row-actions"><Button variant="ghost" size="sm" @click="copy(row)"><Copy class="button-icon" aria-hidden="true" />{{ t('visualGame.promotion.admin.copy') }}</Button><a v-if="row.video_url" :href="row.video_url" target="_blank" rel="noopener noreferrer"><ExternalLink class="button-icon" aria-hidden="true" />{{ t('visualGame.promotion.admin.video') }}</a></div></div></template>
          <template v-for="stage in stages" :key="stage" #[stage]="{ row }"><span class="metric-value" :class="{ 'is-zero': !row.counts[stage] }">{{ row.counts[stage] || 0 }}</span></template>
          <template #actions="{ row }"><div class="row-actions"><Button variant="ghost" size="sm" :disabled="saving || refreshing || toggling_link_id === row.id" @click="edit(row)"><Pencil class="button-icon" aria-hidden="true" />{{ t('visualGame.promotion.admin.edit') }}</Button><Button variant="ghost" size="sm" :loading="toggling_link_id === row.id" :disabled="saving || refreshing || (!!toggling_link_id && toggling_link_id !== row.id)" @click="toggle(row)">{{ t(row.enabled ? 'visualGame.promotion.admin.disableLink' : 'visualGame.promotion.admin.enableLink') }}</Button></div></template>
        </Table>
        <p class="section-note muted">{{ t('visualGame.promotion.admin.countHint') }}</p>
      </Section>
      <Section :title="t('visualGame.promotion.admin.riskTitle')" :padded="false">
        <p class="section-note muted">{{ t('visualGame.promotion.admin.riskHint') }}</p>
        <EmptyState v-if="!data.risks.length" :title="t('visualGame.promotion.admin.noRisk')" />
        <Table v-else :columns="risk_columns" :rows="data.risks">
          <template #created_at="{ row }"><span class="risk-time">{{ new Date(row.created_at).toLocaleString() }}</span></template>
          <template #risk_type="{ row }"><Badge :variant="['trial_enabled', 'trial_disabled'].includes(row.risk_type) ? 'neutral' : 'warning'">{{ t(`visualGame.promotion.admin.risks.${row.risk_type}`) }}</Badge></template>
          <template #run_id="{ row }"><code class="muted">{{ row.run_id || '—' }}</code></template>
        </Table>
      </Section>
    </template>
    <Modal :open="open" :title="t(form.id ? 'visualGame.promotion.admin.editTitle' : 'visualGame.promotion.admin.create')" :close-on-backdrop="!saving" width="640px" @update:open="open = $event">
      <form id="promotion-form" class="promotion-form" @submit.prevent="save">
        <FormField class="form-wide" :label="t('visualGame.promotion.admin.name')" :hint="t('visualGame.promotion.admin.nameHint')"><input v-model="form.name" maxlength="200"></FormField>
        <FormField class="form-wide" data-testid="promotion-game-field" :label="t('visualGame.promotion.admin.game')" required><Select v-model="form.scenario_id" :options="game_options" :disabled="!!form.id" :aria-label="t('visualGame.promotion.admin.game')" :placeholder="t('visualGame.promotion.admin.chooseGame')" :search-placeholder="t('visualGame.promotion.admin.searchGame')" :empty-text="t('visualGame.promotion.admin.noGameMatch')" searchable block /></FormField>
        <FormField class="form-wide" :label="t('visualGame.promotion.admin.platform')" required><select v-model="form.platform"><option v-for="platform in ['TikTok', 'YouTube', 'Instagram', 'Facebook', 'X', 'Douyin', 'Xiaohongshu']" :key="platform">{{ platform }}</option></select></FormField>
        <FormField class="form-wide" :label="t('visualGame.promotion.admin.videoUrl')" :hint="t('visualGame.promotion.admin.videoHint')"><input v-model="form.video_url" type="url" maxlength="2048"></FormField>
      </form>
      <template #footer><Button variant="secondary" :disabled="saving" @click="open = false">{{ t('visualGame.promotion.admin.cancel') }}</Button><Button type="submit" form="promotion-form" :loading="saving" :disabled="!form.scenario_id">{{ t('visualGame.promotion.admin.save') }}</Button></template>
    </Modal>
  </div>
</template>

<style scoped>
.promotions-page { display: grid; gap: var(--drama-space-xl); min-width: 0; }
.page-heading { display: grid; gap: var(--drama-space-sm); }
.promotions-page p, .promotions-page h2, .promotions-page h3 { margin: 0; }
.muted { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); line-height: var(--drama-line-height-normal); }
.button-icon { width: var(--drama-space-lg); height: var(--drama-space-lg); flex-shrink: 0; }
.trial-switch { display: flex; align-items: center; gap: var(--drama-space-lg); }
.panel-icon { display: grid; place-items: center; padding: var(--drama-space-md); border-radius: var(--drama-radius-md); color: var(--drama-color-primary); background: var(--drama-color-surface-alt); flex-shrink: 0; }
.panel-icon.is-enabled { color: var(--drama-color-success); background: color-mix(in srgb, var(--drama-color-success) 10%, var(--drama-color-surface)); }
.panel-icon svg { width: var(--drama-space-xl); height: var(--drama-space-xl); }
.trial-copy { display: grid; gap: var(--drama-space-sm); flex: 1; }
.trial-copy h2 { font-size: var(--drama-font-size-md); font-weight: var(--drama-font-weight-bold); }
.trial-actions { display: flex; align-items: center; gap: var(--drama-space-md); flex-shrink: 0; }
.section-count { padding: var(--drama-space-xs) var(--drama-space-sm); border-radius: var(--drama-radius-sm); background: var(--drama-color-surface-alt); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); font-variant-numeric: tabular-nums; }
.links-empty { display: flex; flex-direction: column; align-items: center; gap: var(--drama-space-md); padding: var(--drama-space-2xl) var(--drama-space-xl); text-align: center; }
.links-empty h3 { font-size: var(--drama-font-size-md); }
.links-empty p { max-width: 40ch; }
.section-note { padding: var(--drama-space-md) var(--drama-space-xl); background: var(--drama-color-surface-alt); }
.link-identity, .link-address { display: grid; gap: var(--drama-space-sm); }
.link-identity :deep(.drama-badge) { justify-self: start; }
.link-address code { overflow-wrap: anywhere; font-size: var(--drama-font-size-xs); }
.row-actions { display: flex; align-items: center; gap: var(--drama-space-xs); }
.row-actions a { display: inline-flex; align-items: center; gap: var(--drama-space-xs); color: var(--drama-color-primary); font-size: var(--drama-font-size-xs); text-decoration: none; }
.row-actions a:hover { text-decoration: underline; }
.row-actions a:focus-visible { outline: 2px solid var(--drama-color-primary); outline-offset: 2px; }
.metric-value { font-variant-numeric: tabular-nums; font-weight: var(--drama-font-weight-bold); }
.is-zero { color: var(--drama-color-text-muted); font-weight: var(--drama-font-weight-normal); }
.risk-time { font-variant-numeric: tabular-nums; font-size: var(--drama-font-size-sm); }
.error-message { padding: var(--drama-space-lg); border: 1px solid var(--drama-color-danger); border-radius: var(--drama-radius-md); color: var(--drama-color-danger); }
.loading-state { display: grid; place-items: center; padding: var(--drama-space-2xl); }
.promotion-form { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--drama-space-xl); }
.form-wide { grid-column: 1 / -1; }
.promotion-form input, .promotion-form select { width: 100%; box-sizing: border-box; min-height: 44px; padding: var(--drama-space-md); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-sm); background: var(--drama-color-surface); color: var(--drama-color-text); font-family: inherit; font-size: var(--drama-font-size-sm); }
.promotion-form input:hover, .promotion-form select:hover { border-color: var(--drama-color-border-strong); }
.promotion-form input:focus-visible, .promotion-form select:focus-visible { outline: 2px solid var(--drama-color-primary); outline-offset: 2px; }
.promotion-form select:disabled { background: var(--drama-color-surface-alt); color: var(--drama-color-text-muted); }
@media (max-width: 1100px) {
  .trial-switch { flex-wrap: wrap; }
  .trial-copy { min-width: 0; flex-basis: 70%; }
  .trial-actions { margin-left: auto; }
}
@media (max-width: 640px) {
  .promotion-form { grid-template-columns: minmax(0, 1fr); }
  .trial-actions { flex-wrap: wrap; margin-left: 0; }
  .page-heading :deep(.drama-page-header) { flex-wrap: wrap; }
}
</style>
